import React, { useState, useEffect, useMemo } from 'react'
import { connect, useDispatch, useSelector, shallowEqual } from 'react-redux'
import { ImageViewer as PDFViewer } from '@tecuity/pdf-viewer'
import { setWindowInstance, setEnvelopeId, setRequestOpen } from './ImageViewerActions'
import ImageGroup from './ImageGroup'
import '@tecuity/pdf-viewer/dist/index.css'
import './ImageViewer.scss'
import CaretLeft from '@tecuity/sos-icons/react/caret-left'
import CaretRight from '@tecuity/sos-icons/react/caret-right'
import { Helmet } from 'react-helmet'
import { useHistory } from 'react-router'
import { BASE_URL, GET_IMAGE_ENVELOPE_BY_ID } from '../../requests'
import { useUserHasRight, useSearchParams } from '../../Utilities/hooks'
import FileDownloader from './FileDownloader'
import * as api from '../../requests'
import { setErrorToast, setSuccessToast } from '../../actions'
import { Spinner } from 'components/Loaders/Loaders'
import { ButtBox } from '@tecuity/granite-ui'
import Cookies from 'js-cookie'

export let ImageViewer = ({
  location,
  match: {
    params: { envelopeId = '', imageViewerDln = '' },
  },
  setWindowInstance,
  ...props
}) => {
  const { appUserRights } = useSelector((state) => state.auth, shallowEqual)
  const { activeGroupId } = useSearchParams(location.search)
  const history = useHistory()
  const dispatch = useDispatch()
  const [sidebarExpanded, setSidebarExpanded] = React.useState(true)
  const [imageGroups, setImageGroups] = React.useState([])
  // const [payments, setPayments] = React.useState(null)
  const [activePageIndex, setActivePageIndex] = React.useState(0)
  const [activePageImageNumber, setActivePageImageNumber] = React.useState(null)
  const [activePageHeight, setActivePageHeight] = React.useState(0)
  const [useHtmlRedaction, setUseHtmlRedaction] = React.useState(false)
  const [bytes, setBytes] = React.useState(null)
  const [downloadURL, setDownloadURL] = React.useState(null)
  const [downloadOnNextTick, setDownloadOnNextTick] = React.useState(false)
  const [showConfirmDelete, setShowConfirmDelete] = React.useState(false)
  const [showConfirmUnredact, setShowConfirmUnredact] = React.useState(false)
  const [showLoadingSpinner, setShowLoadingSpinner] = React.useState(false)
  const [showOriginals, setShowOriginals] = React.useState(false)
  const [redacting, setRedacting] = React.useState(false)
  const downloader = React.useRef()

  useEffect(() => {
    if (!appUserRights.includes('ViewImages')) {
      history.push('/')
    }
  }, [appUserRights])

  const getBlobFromUrl = (myImageUrl) => {
    return new Promise((resolve, reject) => {
      let AUTH = localStorage.getItem('USER_AUTHENTICATION')
      let auth = AUTH && AUTH !== 'undefined' ? JSON.parse(AUTH) : {}

      let specialUserId = Cookies.get('special-user-id')

      if (!auth.ID) {
        console.debug('unable to find user from local storage. Checking cookies...')

        if (specialUserId && specialUserId !== '') {
          console.debug('user found in cookies. attaching credentials.')
          auth.ID = specialUserId
        }
      } else if (specialUserId && specialUserId !== '' && auth.ID !== specialUserId) {
        console.debug(`overwritting auth.ID with MI Login value ${specialUserId}`)
        auth.ID = specialUserId
      }

      let request = new XMLHttpRequest()
      request.open('GET', myImageUrl, true)
      request.setRequestHeader('Authorization', auth.ID)
      request.responseType = 'blob'
      request.onload = () => {
        resolve(request.response)
      }
      request.onerror = reject
      request.send()
    })
  }

  const getDataFromBlob = (myBlob) => {
    return new Promise((resolve, reject) => {
      let reader = new FileReader()
      reader.onload = () => {
        resolve(reader.result)
      }
      reader.onerror = reject
      reader.readAsArrayBuffer(myBlob)
    })
  }

  const handleExport = (bytes) => {
    setDownloadOnNextTick(false)
    const blobObject = window.URL.createObjectURL(
      new Blob([bytes], { type: 'application/octet-stream' })
    )
    setDownloadURL(blobObject)
    setDownloadOnNextTick(true)
  }

  const handlePrint = (bytes) => {
    const blob = new Blob([bytes], { type: 'application/pdf' })
    const blobURL = window.URL.createObjectURL(blob)
    const iframe = document.createElement('iframe') //load content in an iframe to print later
    document.body.appendChild(iframe)
    iframe.style.display = 'none'
    iframe.src = blobURL
    iframe.onload = function () {
      setTimeout(function () {
        iframe.focus()
        iframe.contentWindow.print()
      }, 1)
    }
  }

  const handleRedact = () => {
    if (useHtmlRedaction) {
      window.open(`/image-redaction/${activePageImageNumber}`)
      setRedacting(true)
    }
  }

  const handlePrintQueue = () => {
    api.SEND_IMAGE_TO_PRINT_QUEUE(
      activePageImageNumber,
      () => {
        dispatch(setSuccessToast(`Successfully Sent Image ${activePageImageNumber} to Print Queue`))
      },
      (err) => {
        dispatch(setErrorToast(`Unable to send Image ${activePageImageNumber}`))
        console.error(err.Message)
      }
    )
  }

  const handleSaveImageRedaction = ({ rects, pdfCanvas }) => {
    const scaledElem = document.querySelector('.react-pdf__Document > div:last-of-type')
    const scale = parseFloat(
      (scaledElem.getBoundingClientRect().width / scaledElem.offsetWidth).toFixed(1)
    )
    let multiplier = (activePageHeight / pdfCanvas.getBoundingClientRect().height) * scale

    const newRects = rects.map((rect) => ({
      height: rect.height * multiplier,
      width: rect.width * multiplier,
      top: rect.top * multiplier,
      left: rect.left * multiplier,
    }))

    api.SAVE_IMAGE_REDACTION(
      activePageImageNumber,
      newRects,
      () => {
        dispatch(setSuccessToast(`Successfully Redacted Image ${activePageImageNumber}`))
        window.location.reload()
      },
      (err) => {
        dispatch(setErrorToast(`Unable to Redact Image ${activePageImageNumber}`))
        console.error(err.Message)
      }
    )
  }

  const handleRedactionWindowUnload = () => {
    window.location = window.location.pathname
  }

  useEffect(() => {
    if (redacting) {
      window.addEventListener('focus', handleRedactionWindowUnload)
    }
    return () => {
      window.removeEventListener('focus', handleRedactionWindowUnload)
    }
  }, [redacting])

  useEffect(() => {
    if (downloadOnNextTick) {
      downloader.current.download()
    }
  }, [downloadOnNextTick])

  useEffect(() => {
    window.setEnvelopeId = (id, imageViewerDln = '') => {
      // set selected one to imageViewerDln
      history.replace(`/image-viewer/${id}/${imageViewerDln}`)
    }
    if (
      window.opener &&
      window.opener.getImageViewerImageId &&
      window.opener.getImageViewerImageDln
    ) {
      const imageId = window.opener.getImageViewerImageId()
      const imageDln = window.opener.getImageViewerImageDln()
      if (imageId && imageDln) {
        history.replace(`/image-viewer/${imageId}/${imageDln}`)
        dispatch({
          type: 'SET_ENVELOPE_ID',
          envelopeId: null,
          imageViewerDln: null,
        })
      } else {
        // window.close()
      }
    } else {
      window.close()
    }
  }, [])

  const setImageBytes = async (activeGroupId, showOriginals) => {
    try {
      let myBlob = await getBlobFromUrl(
        `${BASE_URL}/api/ImageEnvelope/GetImage/${activeGroupId}/${showOriginals}`
      )
      let myImageData = await getDataFromBlob(myBlob)
      setBytes(myImageData)
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    if (activeGroupId) {
      setImageBytes(activeGroupId, showOriginals)
      activeGroup && setUseHtmlRedaction(activeGroup.pages[activePageIndex].useHtmlRedaction)
    }
  }, [activeGroupId, showOriginals])

  const selectGroup = React.useCallback(
    (group, pageIndex = 0) => {
      if (pageIndex !== undefined) {
        setActivePageIndex(pageIndex)
      } else {
        setActivePageIndex(0)
      }
      if (group !== undefined) {
        setActivePageImageNumber(group.pages[activePageIndex].DLN)
        setActivePageHeight(group.pages[activePageIndex].dimensions.Y)
      }
      setShowConfirmDelete(false)
      setShowConfirmUnredact(false)
      const groupId = group.groupId ? group.groupId : ''
      history.replace(`${location.pathname}?activeGroupId=${groupId}`)
    },
    [location.pathname]
  )

  useEffect(() => {
    if (envelopeId && !location.search) {
      GET_IMAGE_ENVELOPE_BY_ID(
        envelopeId,
        ({ groups /*payments*/ }) => {
          if (groups) {
            setImageGroups(groups)
            if (imageViewerDln) {
              groups.forEach((group) => {
                const pageIndex = group.pages.findIndex((page) => page.DLN === imageViewerDln)
                if (pageIndex >= 0) {
                  selectGroup(group, pageIndex)
                }
              })
            } else selectGroup(groups[0])
          }
          setShowLoadingSpinner(false)
        },
        (err) => {
          setShowLoadingSpinner(false)
          console.error(err)
        }
      )
    }
  }, [envelopeId, selectGroup, location.search])

  const activeGroup = React.useMemo(() => {
    if (imageGroups && activeGroupId !== undefined) {
      return imageGroups.find((group) => group.groupId.toString() === activeGroupId)
    } else {
      return null
    }
  }, [activeGroupId, imageGroups])

  useEffect(() => {
    if (activeGroup) {
      setActivePageImageNumber(activeGroup.pages[activePageIndex].DLN)
      setActivePageHeight(activeGroup.pages[activePageIndex].dimensions.Y)
      setUseHtmlRedaction(activeGroup.pages[activePageIndex].useHtmlRedaction)
    }
  }, [activePageIndex])

  const canUserDeleteImage = (statusID, tagged) => {
    if (tagged) return false

    switch (statusID) {
      case 549: // Complete/Approved -> Processed.
      case 540: // Rejected -> Processed.
        return appUserRights.includes('DeleteProcessedImages')
      case 559: // Needs Correction -> Existing.
      case 539: // New -> Existing
      case 558: // Pending Review -> Existing
      case 546: // Pending -> Existing
        return appUserRights.includes('DeleteImages')
      default:
        return false
    }
  }

  const deleteImage = (groupStatusID, dln, tagged) => {
    if (canUserDeleteImage(groupStatusID, tagged)) {
      setShowLoadingSpinner(true)
      // 18455 Waiting For Endpoint to be completed.
      api.DELETE_IMAGE_BY_DLN(
        dln,
        (response) => {
          // these two lines may not be needed.... refresh....
          dispatch(setSuccessToast(`Successfully Deleted Image ${dln}`))
          setShowLoadingSpinner(false)
          // refresh window.
          window.location.reload(true)
        },
        (err) => {
          dispatch(setErrorToast(`Failed to Delete Image ${dln}`))
          setShowLoadingSpinner(false)
          console.error(err)
        }
      )
    }
  }

  const canUserUnredactImage = () => {
    return appUserRights.includes('ManipulateImages')
  }

  const unredactImage = (dln) => {
    if (canUserUnredactImage) {
      setShowLoadingSpinner(true)
      api.UNREDACT_IMAGE_BY_DLN(
        dln,
        (response) => {
          dispatch(setSuccessToast(`Successfully Unredacted Image ${dln}`))
          setShowLoadingSpinner(false)
          window.location.reload(true)
        },
        (err) => {
          dispatch(setErrorToast(`Failed to Unredact Image ${dln}`))
          setShowLoadingSpinner(false)
          console.error(err)
        }
      )
    }
  }

  const onRegenerateFiledStamp = () => {
    if (!showRegenerateFiledStamp) return

    setShowLoadingSpinner(true)
    api.REGENERATE_FILED_STAMP(
      activePageImageNumber,
      (res) => {
        dispatch(
          setSuccessToast(`Successfully Regenerated File Stamp for ${activePageImageNumber}`)
        )
        setShowLoadingSpinner(false)
        window.location.reload(true)
      },
      (err) => {
        dispatch(setErrorToast(`Failed to Regenerate Filed Stamp for ${activePageImageNumber}`))
        setShowLoadingSpinner(false)
        console.error(err)
      }
    )
  }

  const showRegenerateFiledStamp = useMemo(() => {
    if (!activeGroup) return false

    // The user has the associated right AND
    // The image is in an approved status AND
    // The image is the first page AND
    // The image is not a payment.
    return (
      appUserRights.includes('RegenerateFile') &&
      activeGroup.statusID === 549 /* Approved */ &&
      activePageIndex === 0 &&
      activeGroup.pages[activePageIndex].type !== 'Payment'
    )
  }, [appUserRights, activeGroup, activePageIndex])

  return (
    <div className="envelope-wrapper" data-sidebar-collapsed={!sidebarExpanded}>
      <Helmet>
        <style type="text/css">{`body{overflow: hidden;}`}</style>
      </Helmet>
      <nav
        className="envelope-sidebar"
        style={{ transform: !sidebarExpanded ? 'translateX(0px)' : '', marginTop: '35px' }}
      >
        <div className="sidebar-header">
          Work Items{' '}
          <button onClick={() => setSidebarExpanded(false)}>
            <CaretLeft />
          </button>
        </div>
        {showLoadingSpinner ? (
          <Spinner color="#7792d0" className="entity-step-loader" size="small" />
        ) : (
          imageGroups.map((group) => (
            <ImageGroup
              {...group}
              isActive={activeGroupId === group.groupId.toString()}
              activePageIndex={activePageIndex}
              onSelectGroup={() => selectGroup(group)}
              onSelectPage={(pageIndex) => selectGroup(group, pageIndex)}
              key={group.groupId}
              statusID={group.statusID}
              canUserDeleteImage={canUserDeleteImage}
              showConfirmDelete={showConfirmDelete}
              setShowConfirmDelete={setShowConfirmDelete}
              deleteImage={deleteImage}
              canUserUnredactImage={canUserUnredactImage}
              showConfirmUnredact={showConfirmUnredact}
              setShowConfirmUnredact={setShowConfirmUnredact}
              unredactImage={unredactImage}
            />
          ))
        )}
        {useUserHasRight('IMAGING_SHOW_ORIGINAL_IMAGES')}
        <aside className="show-originals-checkbox">
          <ButtBox
            label="Show Originals"
            checked={showOriginals}
            onChange={() => setShowOriginals(!showOriginals)}
          />
        </aside>

        {/* {payments ? (
          <aside className="payments-block">
            <dl className="payments-inner-wrapper">
              <div className="payment-col" data-p-type="charges">
                <dt>Charges</dt>
                <dd>
                  <span className="payment-prefix">$</span>
                  {getPaymentAmount(payments.charges.amount)}
                </dd>
              </div>
              <div className="payment-col" data-p-type="payments">
                <dt>Payments</dt>
                <dd>
                  <span className="payment-prefix">$</span>
                  {getPaymentAmount(payments.payments.amount)}
                </dd>
              </div>
              <div className="payment-col">
                <dt>Balance</dt>
                <dd>
                  <span className="payment-prefix">$</span>
                  {getPaymentAmount(payments.balance.amount)}
                </dd>
              </div>
            </dl>
          </aside>
        ) : null} */}
      </nav>
      {activeGroupId && activeGroup && (
        <div className="image-viewer-wrapper" data-envelope-collapsed={!sidebarExpanded}>
          <PDFViewer
            pdfBytes={bytes}
            title={activeGroup.name}
            onExport={handleExport}
            onPrint={handlePrint}
            onRedact={handleRedact}
            onSendToPrintQueue={handlePrintQueue}
            useHtmlRedaction={useHtmlRedaction}
            onSaveImageRedaction={handleSaveImageRedaction}
            thumbnailWidth={140}
            imageViewerRights={{
              hasEditRights: appUserRights.includes('EditImages'),
              hasPrintRights: appUserRights.includes('PrintImages'),
              hasExportRights: appUserRights.includes('ExportImages'),
              hasPrintQueueRights: appUserRights.includes('PrintQueuePrintEntries'),
              RegenerateFiledStamp: showRegenerateFiledStamp,
            }}
            thumbnailWrapperChildren={
              <button className="expand-envelope-button" onClick={() => setSidebarExpanded(true)}>
                Expand Envelope
                <CaretRight />
              </button>
            }
            redactionURL={`${BASE_URL}/image-redaction/${activePageImageNumber}`}
            currentPageIndex={activePageIndex}
            onCurrentPageIndexChange={(page) => {
              setActivePageIndex(page)
              setShowConfirmDelete(false)
              setShowConfirmUnredact(false)
            }}
            onRegenerateFiledStamp={onRegenerateFiledStamp}
          />
          <FileDownloader
            fileName={`${activeGroup.name}-${envelopeId}.pdf`}
            href={downloadURL}
            ref={downloader}
          />
        </div>
      )}
    </div>
  )
}
ImageViewer = connect((state) => ({ imageId: state.imageViewer.imageId }), {
  setEnvelopeId,
  setWindowInstance,
})(ImageViewer)

export let ImageViewerController = () => {
  const { requestOpen, envelopeId, imageViewerDln } = useSelector((state) => state.imageViewer)
  const { appUserRights } = useSelector((state) => state.auth, shallowEqual)
  const dispatch = useDispatch()
  const [childWindow, setChildWindow] = useState(null)

  useEffect(() => {
    window.getImageViewerImageId = () => {
      return envelopeId
    }
    window.getImageViewerImageDln = () => {
      return imageViewerDln
    }

    if (requestOpen) {
      dispatch(setRequestOpen(false))

      if (appUserRights.includes('ViewImages')) {
        if (childWindow && !childWindow.closed) {
          childWindow.setEnvelopeId(envelopeId, imageViewerDln)
        } else {
          const windowWidth = window.screen.width / 1.5 > 1000 ? window.screen.width / 1.5 : 1000
          const newWindow = window.open(
            `/image-viewer/${envelopeId}/${imageViewerDln}`,
            '_blank',
            `menubar=no,width=${windowWidth},height=${window.screen.height}`
          )
          if (newWindow) {
            newWindow.moveTo(window.screen.width / 2, 0)
            setChildWindow(newWindow)
          }
        }
      }
    }
  }, [requestOpen])

  useEffect(() => {
    const handleTabClose = (ev) => {
      if (childWindow && !childWindow.closed) {
        childWindow.close()
      }
    }
    window.addEventListener('beforeunload', handleTabClose)
    return () => window.removeEventListener('beforeunload', handleTabClose)
  }, [childWindow])

  return null
}
ImageViewerController = connect(
  (state) => ({
    envelopeId: state.imageViewer.envelopeId,
    imageViewerDln: state.imageViewer.imageViewerDln,
    windowInstance: state.imageViewer.windowInstance,
  }),
  { setWindowInstance, setEnvelopeId }
)(ImageViewerController)
